import "./Table.scss";

import {
	useEffect,
	useRef,
	useState,
	FunctionComponent,
	PropsWithChildren,
	useMemo,
} from "react";

export declare type TableProps = PropsWithChildren<{
	data: Record<string, string | number>[];
	keys: string[];
	titles?: string[];
	formats?: Record<string, (v: string | number) => string>;
	rows?: number;
	noRowsMessage?: string;
	highlightOnUpdate?: boolean;
}>;

export const Table: FunctionComponent<TableProps> = ({
	data,
	keys,
	formats = {},
	rows = 5,
	titles,
	noRowsMessage = "no data",
	highlightOnUpdate = false,
}: TableProps) => {
	const [start, setStart] = useState(0);
	const [end, setEnd] = useState(rows);
	const [page, setPage] = useState(0);
	const [pages, setPages] = useState(Math.ceil(data.length / rows));
	const [triggerAnimation, setTriggerAnimation] = useState(false);
	const prevEl = useRef(null);

	const bodyRef = useRef<HTMLDivElement>(null);
	const range = (end, start, step = 1) =>
		Array.from(
			{ length: Math.ceil((end - start) / step) },
			(_, i) => i * step + start
		);

	if (!keys.every((key) => data.every((row) => key in row)))
		throw new Error("invalid data keys");

	const onPrevious = () => {
		let nextStart = start - (end - start);
		if (nextStart < 0) nextStart = 0;
		setStart(nextStart);
		setEnd(nextStart + (end - start));
	};

	const onNext = () => {
		let nextEnd = end + (end - start);
		if (nextEnd > data.length) nextEnd = data.length;
		setStart(nextEnd - (end - start));
		setEnd(nextEnd);
	};

	const formatted = (key: string, value: number | string): string => {
		if (key in formats) {
			return formats[key](value);
		}
		return value.toString();
	};

	const highlightedNumber = useMemo(
		() => Math.floor(Math.random() * 3),
		[data]
	);

	useEffect(() => {
		let timeout;
		if (data?.length && highlightOnUpdate) {
			if (prevEl.current !== JSON.stringify(data[0])) {
				setTriggerAnimation(true);
				const timeout = setTimeout(() => {
					setTriggerAnimation(false);
				}, 2000);
				prevEl.current = JSON.stringify(data[0]);
			}
		}

		return () => {
			timeout && clearTimeout(timeout);
		};
	}, [data]);

	return (
		<div className="table">
			<div className="frame">
				<div className="head">
					{keys.map((key, idx) => {
						return (
							<div
								key={`h-${idx}`}
								className={["heading", `heading-${idx}`, key].join(" ")}
							>
								<p>{titles ? titles[idx] : key}</p>
							</div>
						);
					})}
				</div>
				<div className="body" ref={bodyRef}>
					{data.length ? (
						range(end, start).map((idx) => {
							const obj = data[idx];
							if (!obj) return null;
							return (
								<div
									className={`row ${
										triggerAnimation && highlightedNumber >= idx
											? "highlight-row"
											: ""
									}`}
									key={`row${idx}`}
								>
									{keys.map((key, kidx) => {
										return (
											<div
												key={`${idx}-${kidx}`}
												className={[
													`col`,
													`row-${idx}`,
													`col-${kidx}`,
													`itm-${idx}-${kidx}`,
												].join(" ")}
											>
												<p>{formatted(key, obj[key])}</p>
											</div>
										);
									})}
								</div>
							);
						})
					) : (
						<div className="row no-data" key={`row0`}>
							<div
								key={`0-0`}
								className={[`col`, `row-0`, `col-0`, `itm-0-0`].join(" ")}
							>
								<p>{noRowsMessage}</p>
							</div>
							<div
								key={`0-1`}
								className={[`col`, `row-0`, `col-1`, `itm-0-1`].join(" ")}
							>
								<p>N/A</p>
							</div>
						</div>
					)}
				</div>
			</div>
			{data.length > rows && (
				<div className="table-meta">
					<div className="display">
						<p>
							Showing {start + 1} to {end} of {data.length} results
						</p>
					</div>
					<div className="pagenav">
						<div onClick={onPrevious} className="clickable">
							<p>previous</p>
						</div>
						<div onClick={onNext} className="clickable">
							<p>next</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
